// src/components/SellerApplications.js
import React, { useEffect, useState } from "react";
import "../styles/SellerApplications.css";
import config from "../config";

function generateTempPassword(length = 8) {
  const chars =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let pwd = "";
  for (let i = 0; i < length; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pwd;
}

export default function SellerApplications() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [approvedWithPassword, setApprovedWithPassword] = useState(null);

  // Fetch pending seller applications from backend
  const loadPending = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${config.baseUrl}/api/admin/sellers/applications/pending`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load pending applications");
      }

      const data = await response.json();
      setPending(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading pending applications:", err);
      setError("Failed to load applications: " + err.message);
      setPending([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const approveSeller = async (applicationId, sellerId) => {
    try {
      const response = await fetch(
        `${config.baseUrl}/api/admin/sellers/applications/${applicationId}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to approve seller");
      }

      const result = await response.json();
      console.log("Seller approved:", result);

      // Show temp password to admin
      setApprovedWithPassword({
        sellerId: result.sellerId,
        tempPassword: result.tempPassword,
        applicationId: result.applicationId,
      });

      // Refresh pending list
      await loadPending();
    } catch (err) {
      console.error("Approval error:", err);
      alert("❌ Failed to approve seller: " + err.message);
    }
  };

  const rejectSeller = async (applicationId) => {
    if (!window.confirm("Reject this seller application?")) return;

    try {
      const response = await fetch(
        `${config.baseUrl}/api/admin/sellers/applications/${applicationId}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to reject seller");
      }

      alert("✅ Seller application rejected");
      await loadPending();
    } catch (err) {
      console.error("Rejection error:", err);
      alert("❌ Failed to reject seller: " + err.message);
    }
  };

  return (
    <div className="seller-app-container">
      <h2>🏪 Seller Applications</h2>

      {/* Temp Password Display Modal */}
      {approvedWithPassword && (
        <div className="password-modal">
          <div className="password-card">
            <h3>✅ Seller Approved!</h3>
            <p>Share these credentials with the seller:</p>
            <div className="password-box">
              <p>
                <strong>Seller ID:</strong> {approvedWithPassword.sellerId}
              </p>
              <p>
                <strong>Temporary Password:</strong>
              </p>
              <div className="password-display">
                <code>{approvedWithPassword.tempPassword}</code>
                <button
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      approvedWithPassword.tempPassword
                    );
                    alert("Copied to clipboard!");
                  }}
                >
                  📋 Copy
                </button>
              </div>
              <p style={{ fontSize: "12px", color: "#666" }}>
                Seller should use their email and this password to login.
              </p>
            </div>
            <button
              className="close-modal-btn"
              onClick={() => setApprovedWithPassword(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && <p className="loading">Loading applications...</p>}

      {/* Error State */}
      {error && <div className="error-message">{error}</div>}

      {/* Applications List */}
      {!loading && pending.length === 0 ? (
        <p className="no-applications">✨ No pending applications.</p>
      ) : (
        <div className="seller-app-grid">
          {pending.map((app) => (
            <div className="seller-card" key={app.applicationId || app.id}>
              <div className="card-header">
                <h3>{app.shopName}</h3>
                <span className="app-id">ID: {app.applicationId || app.id}</span>
              </div>

              <div className="card-body">
                <p>
                  <strong>Contact:</strong> {app.contactName}
                </p>
                <p>
                  <strong>Email:</strong> {app.email}
                </p>
                <p>
                  <strong>Phone:</strong> {app.phoneNumber || "-"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {app.addressLine
                    ? `${app.addressLine}, ${app.city || ""}, ${app.state || ""} - ${app.pincode || ""}`
                    : "-"}
                </p>
                <p>
                  <strong>Category:</strong> {app.primaryCategory || "-"}
                </p>
                <p>
                  <strong>GST:</strong> {app.gstNumber || "-"}
                </p>
              </div>

              <div className="card-actions">
                <button
                  className="approve-btn"
                  onClick={() =>
                    approveSeller(
                      app.applicationId || app.id,
                      app.sellerId || app.id
                    )
                  }
                >
                  ✓ Approve
                </button>
                <button
                  className="reject-btn"
                  onClick={() => rejectSeller(app.applicationId || app.id)}
                >
                  ✕ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
